/*
 * ОЗВУЧКА ТЕОРИИ — живой русский голос для мультиков урока.
 *
 * Перенесено из Майнкрафтии 2.0 (frontend/src/lab/movie/speech.ts) с двумя
 * изменениями: TypeScript → JS и адрес TTS задаётся константой, а не
 * переменной сборки Vite (редактор собирается CRA, у него другой механизм).
 *
 * ДВА ИСТОЧНИКА ГОЛОСА:
 *   1. Серверный Silero (/api/v2/learning/tts) — живой русский голос.
 *      Проверено: ручка отвечает WAV и отдаёт CORS на домен редактора.
 *   2. Браузерный speechSynthesis — запасной, если сервер недоступен.
 *      Немой мультик хуже, чем чуть менее приятный голос.
 *
 * speakAsync ждёт конца речи — кадры сменяются в такт голосу, а не по таймеру.
 */

/** Адрес Silero-TTS школы. Домен явный: редактор живёт на cad.*, а сервис — на mc2-api.*. */
const TTS_BASE = 'https://mc2-api.minecraftia-school.ru/api/v2/learning/tts';

/*
 * Версия озвучки. Бампать ВМЕСТЕ с VOICE_VER на бэкенде при правке произношения:
 * параметр уходит в URL, иначе браузер отдаёт старый WAV из медиа-кэша,
 * который не чистится даже жёсткой перезагрузкой.
 */
const VOICE_VER = 6;

let current = null;
const urlCache = new Map();

/*
 * ── РАЗБЛОКИРОВКА АВТОПЛЕЯ ──
 *
 * Браузер разрешает Audio.play() только при «активации пользователя», и она
 * живёт около пяти секунд после клика. Мультик может стартовать позже: пока
 * грузится WAV, окно активации истекает, и первый play() отклоняется без звука.
 *
 * Поэтому на ПЕРВЫЙ жест пользователя (любой, в любом месте страницы) греем
 * аудио немым звуком. После этого браузер помечает медиа разрешённым на весь
 * сеанс, и озвучка стартует независимо от того, сколько прошло с клика.
 */
let audioUnlocked = false;

function unlockAudio() {
    if (audioUnlocked) return;
    audioUnlocked = true;
    try {
        // Немой WAV (44 байта) — разблокирует HTMLAudioElement.play() на сеанс.
        const a = new Audio(
            'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=',
        );
        a.volume = 0;
        a.play().then(() => a.pause()).catch(() => {});
    } catch (_) { /* без аудио — не страшно */ }
    try { window.speechSynthesis?.resume(); } catch (_) { /* нет синтеза */ }
}

if (typeof window !== 'undefined') {
    const opts = { once: true, capture: true };
    window.addEventListener('pointerdown', unlockAudio, opts);
    window.addEventListener('keydown', unlockAudio, opts);
    window.addEventListener('touchstart', unlockAudio, opts);
}


function stopCurrent() {
    if (current) {
        current.cancelled = true;
        if (current.audio) {
            current.audio.pause();
            current.audio.onended = null;
            current.audio.onerror = null;
        }
        if (current.browser) window.speechSynthesis?.cancel();
    }
    current = null;
}

/** Русский голос браузера — для запасного пути. */
let voice = null;

function pickRu() {
    if (voice) return voice;
    const all = window.speechSynthesis?.getVoices?.() || [];
    voice = all.find((v) => v.lang?.toLowerCase().startsWith('ru')) || null;
    return voice;
}

if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.onvoiceschanged = () => { voice = null; pickRu(); };
}

function browserSpeak(text, onend) {
    const synth = window.speechSynthesis;
    if (!synth) { onend?.(); return; }
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'ru-RU';
    const v = pickRu();
    if (v) u.voice = v;
    u.rate = 1.0;
    u.pitch = 1.05;
    u.onend = () => onend?.();
    u.onerror = () => onend?.();
    current = { browser: true, cancelled: false };
    synth.speak(u);
}

async function fetchTts(text) {
    if (urlCache.has(text)) return urlCache.get(text);
    const res = await fetch(`${TTS_BASE}?text=${encodeURIComponent(text)}&v=${VOICE_VER}`);
    if (!res.ok) throw new Error(`tts ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    urlCache.set(text, url);
    return url;
}

function play(text, rate, onend) {
    stopCurrent();
    if (!text) { onend?.(); return null; }

    const audio = new Audio();
    audio.playbackRate = rate;
    const token = { audio, cancelled: false };
    current = token;

    fetchTts(text)
        .then((url) => {
            if (token.cancelled) return;
            audio.src = url;
            audio.playbackRate = rate;
            audio.onended = () => { if (!token.cancelled) onend?.(); };
            // Сервер отдал битый WAV — переходим на браузерный голос.
            audio.onerror = () => { if (!token.cancelled) browserSpeak(text, onend); };
            audio.play().catch(() => {
                if (!token.cancelled) browserSpeak(text, onend);
            });
        })
        .catch(() => { if (!token.cancelled) browserSpeak(text, onend); });

    return audio;
}

/**
 * Проговорить фразу. Возвращает ручку: промис конца речи + управление.
 * @returns {{promise: Promise<void>, cancel, pause, resume, setRate, audio}}
 */
export function speakAsync(text, rate = 1) {
    let audio = null;
    const promise = new Promise((resolve) => { audio = play(text, rate, resolve); });
    return {
        promise,
        cancel: () => stopSpeaking(),
        pause: () => {
            audio?.pause();
            if (window.speechSynthesis) window.speechSynthesis.pause();
        },
        resume: () => {
            audio?.play().catch(() => {});
            if (window.speechSynthesis) window.speechSynthesis.resume();
        },
        setRate: (r) => { if (audio) audio.playbackRate = r; },
        audio,
    };
}

export function stopSpeaking() { stopCurrent(); }

/**
 * Загрузить озвучку наперёд, чтобы при смене кадра голос звучал сразу,
 * без паузы на скачивание.
 */
export function preloadVoices(texts) {
    const uniq = [...new Set((texts || []).filter((t) => t && t.trim()))];
    return Promise.all(
        uniq.map((t) => fetchTts(t.trim().slice(0, 900)).catch(() => null)),
    ).then(() => undefined);
}

/** Готова ли фраза (WAV уже в кэше) — чтобы показать крутилку. */
export function isVoiceReady(text) {
    return urlCache.has((text || '').trim().slice(0, 900));
}
